import { useState, useEffect } from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAdminApi, Company } from "@/hooks/useAdminApi";
import { Plus, Building2, Pencil, Trash2, Search, Loader2, Users, RefreshCw, Check, AlertCircle, Key, AlertTriangle, Server } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function AdminCompanies() {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const { toast } = useToast();
  const { listCompanies, createCompany, updateCompany, deleteCompany, syncCompany, regenerateCompanyApiKey } = useAdminApi();
  
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [formName, setFormName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [syncingCompanyId, setSyncingCompanyId] = useState<string | null>(null);

  const loadCompanies = async () => {
    setLoading(true);
    const result = await listCompanies({ page, limit, search });
    if (result.success && result.data) {
      setCompanies(result.data);
      setTotal(result.total || 0);
    } else {
      toast({
        title: tc('error'),
        description: result.error || t('companies.errorLoadCompanies'),
        variant: "destructive",
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    loadCompanies();
  }, [page, search]);

  const handleCreate = async () => {
    if (!formName.trim()) return;
    
    setSubmitting(true);
    const result = await createCompany(formName);
    
    if (result.success) {
      toast({ title: tc('success'), description: t('companies.companyCreated') });
      setCreateDialogOpen(false);
      setFormName("");
      loadCompanies();
    } else {
      toast({
        title: tc('error'),
        description: result.error || t('companies.errorCreate'),
        variant: "destructive",
      });
    }
    setSubmitting(false);
  };

  const handleEdit = async () => {
    if (!selectedCompany || !formName.trim()) return;
    
    setSubmitting(true);
    const result = await updateCompany(selectedCompany.id, { name: formName });
    
    if (result.success) {
      toast({ title: tc('success'), description: t('companies.companyUpdated') });
      setEditDialogOpen(false);
      setSelectedCompany(null);
      setFormName("");
      loadCompanies();
    } else {
      toast({
        title: tc('error'),
        description: result.error || t('companies.errorUpdate'),
        variant: "destructive",
      });
    }
    setSubmitting(false);
  };

  const handleToggleActive = async (company: Company) => {
    const result = await updateCompany(company.id, { is_active: !company.is_active });
    
    if (result.success) {
      toast({ 
        title: tc('success'), 
        description: company.is_active ? t('companies.companyDeactivated') : t('companies.companyActivated')
      });
      loadCompanies();
    } else {
      toast({
        title: tc('error'),
        description: result.error || t('companies.errorUpdate'),
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (!selectedCompany) return;
    
    setSubmitting(true);
    const result = await deleteCompany(selectedCompany.id);
    
    if (result.success) {
      const usersDeactivated = (result as { users_deactivated?: number }).users_deactivated;
      const usersMsg = usersDeactivated && usersDeactivated > 0
        ? ` (${t('companies.usersDeactivated', { count: usersDeactivated })})`
        : '';
      toast({ title: tc('success'), description: `${t('companies.companyDeleted')}${usersMsg}` });
      setDeleteDialogOpen(false);
      setSelectedCompany(null);
      loadCompanies();
    } else {
      toast({
        title: tc('error'),
        description: result.error || t('companies.errorDelete'),
        variant: "destructive",
      });
    }
    setSubmitting(false);
  };

  const openEditDialog = (company: Company) => {
    setSelectedCompany(company);
    setFormName(company.name);
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (company: Company) => {
    setSelectedCompany(company);
    setDeleteDialogOpen(true);
  };

  const handleSync = async (company: Company) => {
    setSyncingCompanyId(company.id);
    const result = await syncCompany(company.id);
    
    if (result.success) {
      toast({ 
        title: tc('success'), 
        description: t('companies.companySynced', { name: company.name })
      });
      loadCompanies();
    } else {
      toast({
        title: tc('error'),
        description: result.error || t('companies.errorSync'),
        variant: "destructive",
      });
    }
    setSyncingCompanyId(null);
  };

  const handleRegenerateApiKey = async () => {
    if (!selectedCompany) return;
    
    setSubmitting(true);
    const result = await regenerateCompanyApiKey(selectedCompany.id);
    
    if (result.success) {
      toast({ 
        title: tc('success'), 
        description: t('companies.keyRegenerated')
      });
      setRegenerateDialogOpen(false);
      loadCompanies();
    } else {
      toast({
        title: tc('error'),
        description: (result as { details?: string }).details || result.error || t('companies.errorRegenerate'),
        variant: "destructive",
      });
    }
    setSubmitting(false);
  };

  const openRegenerateDialog = (company: Company) => {
    setSelectedCompany(company);
    setRegenerateDialogOpen(true);
  };

  const handleEnvironmentChange = async (company: Company, newEnv: 'prod' | 'beta') => {
    const result = await updateCompany(company.id, { api_environment: newEnv });
    
    if (result.success) {
      toast({ 
        title: tc('success'), 
        description: t('companies.envChanged', { name: company.name, env: newEnv.toUpperCase() })
      });
      loadCompanies();
    } else {
      toast({
        title: tc('error'),
        description: result.error || t('companies.errorChangeEnv'),
        variant: "destructive",
      });
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t('companies.title')}</h1>
            <p className="text-muted-foreground">
              {t('companies.subtitle')}
            </p>
          </div>
          <Button onClick={() => { setFormName(""); setCreateDialogOpen(true); }}>
            <Plus className="mr-2 h-4 w-4" />
            {t('companies.addCompany')}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                {t('companies.companyList')} ({total})
              </CardTitle>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t('companies.searchPlaceholder')}
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="pl-9"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : companies.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                {search ? t('companies.noResults') : t('companies.noCompanies')}
              </p>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('companies.companyName')}</TableHead>
                      <TableHead className="text-center">{t('companies.users')}</TableHead>
                      <TableHead className="text-center">{t('companies.environment')}</TableHead>
                      <TableHead className="text-center">{t('companies.api')}</TableHead>
                      <TableHead className="text-center">{t('companies.apiKey')}</TableHead>
                      <TableHead className="text-center">{t('companies.status')}</TableHead>
                      <TableHead>{t('companies.createdAt')}</TableHead>
                      <TableHead className="text-right">{t('companies.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {companies.map((company) => (
                      <TableRow key={company.id}>
                        <TableCell className="font-medium">{company.name}</TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Users className="h-4 w-4 text-muted-foreground" />
                            <span>{company.users_count || 0}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Select
                            value={company.api_environment || 'prod'}
                            onValueChange={(value) => handleEnvironmentChange(company, value as 'prod' | 'beta')}
                          >
                            <SelectTrigger className="w-24 h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="prod">
                                <div className="flex items-center gap-1">
                                  <Server className="h-3 w-3 text-green-600" />
                                  <span>PROD</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="beta">
                                <div className="flex items-center gap-1">
                                  <Server className="h-3 w-3 text-amber-500" />
                                  <span>BETA</span>
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-center">
                          {company.is_synced || company.external_api_id ? (
                            <Badge variant="default" className="bg-green-600">
                              <Check className="h-3 w-3 mr-1" />
                              {t('companies.synced')}
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              {t('companies.notSynced')}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {company.has_api_key || company.company_api_key ? (
                            <Badge variant="default" className="bg-blue-600">
                              <Key className="h-3 w-3 mr-1" />
                              {t('companies.keyActive')}
                            </Badge>
                          ) : company.is_synced || company.external_api_id ? (
                            <Badge variant="secondary" className="bg-orange-100 text-orange-700 hover:bg-orange-100">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              {t('companies.keyMissing')}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Switch
                            checked={company.is_active}
                            onCheckedChange={() => handleToggleActive(company)}
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(company.created_at).toLocaleDateString('pl-PL')}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {!company.is_synced && !company.external_api_id && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleSync(company)}
                                disabled={syncingCompanyId === company.id}
                                title={t('companies.syncWithApi')}
                              >
                                <RefreshCw className={`h-4 w-4 ${syncingCompanyId === company.id ? 'animate-spin' : ''}`} />
                              </Button>
                            )}
                            {(company.is_synced || company.external_api_id) && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openRegenerateDialog(company)}
                                title={t('companies.regenerateKey')}
                              >
                                <Key className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditDialog(company)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openDeleteDialog(company)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-muted-foreground">
                      {tc('page')} {page} {tc('of')} {totalPages}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        {tc('previous')}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        {tc('next')}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('companies.newCompany')}</DialogTitle>
            <DialogDescription>
              {t('companies.newCompanyDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('companies.companyName')}</Label>
              <Input
                id="name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder={t('companies.companyNamePlaceholder')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              {tc('cancel')}
            </Button>
            <Button onClick={handleCreate} disabled={!formName.trim() || submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {tc('create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('companies.editCompany')}</DialogTitle>
            <DialogDescription>
              {t('companies.editCompanyDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">{t('companies.companyName')}</Label>
              <Input
                id="edit-name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder={t('companies.companyNamePlaceholder')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              {tc('cancel')}
            </Button>
            <Button onClick={handleEdit} disabled={!formName.trim() || submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {tc('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('companies.deleteCompany')}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>{t('companies.deleteCompanyDesc', { name: selectedCompany?.name })}</p>
                {selectedCompany?.users_count && selectedCompany.users_count > 0 ? (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-2 text-amber-800">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                      <span className="font-medium">
                        {t('companies.usersDeactivated', { count: selectedCompany.users_count })}
                      </span>
                    </div>
                  </div>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tc('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {tc('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Regenerate API Key Confirmation Dialog */}
      <AlertDialog open={regenerateDialogOpen} onOpenChange={setRegenerateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('companies.regenerateKey')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('companies.regenerateKeyDesc', { name: selectedCompany?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tc('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleRegenerateApiKey} disabled={submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('companies.regenerateKey')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainLayout>
  );
}
